<template lang="pug">
  BField(
    :message="canceled ? originalMessage : message"
    :type="messageClass"
    :addons="false"
  )
    WtLabel(
      slot="label"
      :required="required"
      :text="label"
      :tooltip-text="tooltipText"
      :subLabel="subLabel"
    )
    BField.is-marginless.has-addons
      .control(:class="inputClass")
        input.input(
          :placeholder="placeholder"
          :class="messageClass"
          :readonly="readonly && !editName"
          :disabled="disabled"
          :value="value"
          @input="$emit('input', $event.target.value)"
        )
        BIcon(
          v-show="message"
          :class="iconClass"
          :type="!loading ? isValidName ? 'is-success' : 'is-danger' : ''"
          :icon="!loading ? isValidName ? 'check' : 'alert-circle' : ''"
          size="24"
        )
      .control(v-if="isUrl")
        a.button.is-static
         .is-size-5 .thrives.app
      WtReadonlyInputControls(
        v-if="readonly"
        :disabled="loading || !isValidName"
        :edit="editName"
        @editActive="$event => { editName = $event; canceled = false; }"
        @update="updateName"

      )
      .control(v-if="showCancel && editName")
        WtButton(
          @click.native="editName = false; canceled = true; $emit('cancel', originalValue)"
          color="red"
        ) CANCEL
</template>

<script>
import { mapActions } from "vuex";
import WtButton from "@@/WtButton/WtButton";
import WtLabel from "@@/WtLabel/WtLabel";
import WtReadonlyInputControls from "@@/WtReadonlyInputControls/WtReadonlyInputControls";

const VALID_ENTITIES = [
  "teamName",
  "memberUsername",
  "memberEmail",
  "programName",
  "programUrl",
  "challengeName",
  "packageName",
  "templateSubject"
];

/**
 * Input to check uniqueness on emails, usernames,
 * challenge names, team names, and other
 * entities throughout the site.
 */
export default {
  name: "WtUniquenessCheckInput",

  components: { WtLabel, WtReadonlyInputControls, WtButton },

  props: {
    label: {
      type: String,
      default: "",
      loading: false
    },
    /**
     * text below main label
     */
    subLabel: {
      type: String,
      default: ""
    },

    placeholder: {
      type: String,
      required: true
    },

    required: {
      type: Boolean,
      default: true
    },

    /**
     * entity to check uniqueness on, see VALID_ENTITIES
     */
    entity: {
      type: String,
      required: true,
      validator(entity) {
        return VALID_ENTITIES.indexOf(entity) !== -1;
      }
    },

    value: {
      type: String,
      default: ""
    },

    /**
     * Similar to previewMode, makes input readonly but
     * turns on ability to edit the value when icon
     * is clicked
     */
    readonly: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },

    tooltipText: {
      type: String,
      default: ""
    },

    isUrl: {
      type: Boolean,
      default: false
    },

    showCancel: {
      type: Boolean,
      default: false
    },

    triggerUniquenessCheckOnMount: {
      type: Boolean,
      default: false
    },

    programId: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      loading: false,
      editName: false,
      canceled: false,
      isValidName: null,
      originalValue: null,
      originalMessage: "not-set-yet",
      originalValidity: "not-set-yet",
      message: ""
    };
  },

  computed: {
    messageClass() {
      if (this.message) {
        if (!this.isValidName) {
          return "is-danger";
        } else {
          return "is-success";
        }
      }
      return "";
    },

    inputClass() {
      let classString = "is-expanded";
      classString = `${classString} has-icons-right`;
      if (this.loading && !this.canceled) {
        classString = `${classString} is-loading`;
      }
      return classString;
    },

    iconClass() {
      return "is-right";
    }
  },

  watch: {
    value(val) {
      if (this.isOriginalValue(val)) {
        this.setAndEmitNameValidity(this.originalValidity);
        this.message = this.originalMessage;
        this.isValidName = this.originalValidity;
        // TODO revisit this, this is a work-around for when editing a team name and the user
        // decides to enter the original name again.  that is, change name, change name back to original
        // same hold true for challenge name etc.
        // looks like there is work to do with the other watchers (isValidName, message) and the logic around originalValidity, originalMessage
        // NOTE: this is when the ORIGNAL name is in use, so that "available" does not show up in the message, if passing a fresh name we'd want the original message to show
        if (this.entity === "teamName") {
          // this.message = "";
          // this.isValidName = true;
        }
      } else {
        this.triggerUniquenessCheck(val);
      }
    },

    message(message) {
      if (this.originalMessage === "not-set-yet") {
        this.originalMessage = message;
      }
    },

    isValidName(validity) {
      if (this.originalValidity === "not-set-yet") {
        this.originalValidity = validity;
      }
    }
  },

  async mounted() {
    // TODO revist this originalValue logic alongside the value watcher above.  possibly pass in the originalValue as a prop
    await this.$nextTick(); // ensures this.originalValue is set correctly
    this.originalValue = this.value;

    this.triggerUniquenessCheck = this._.debounce(this.uniquenessCheck, 500);

    if (this.triggerUniquenessCheckOnMount) {
      this.uniquenessCheck(this.value, false);
    }
  },

  methods: {
    ...mapActions(["checkUniqueness"]),

    triggerUniquenessCheck() {
      // because we are using debounce here, it needs to be
      // assigned as a function when the component mounts
      // in order for it to work (due to the nature
      // of how debounce is implemented by lodash)
    },

    async uniquenessCheck(value, originalValueCheck = true) {
      const query = value;

      if (query.length < 5) {
        this.message = `${this.label} must be at least 5 characters`;
        this.setAndEmitNameValidity(false);
        return;
      }

      if (originalValueCheck && this.isOriginalValue(query)) {
        this.setAndEmitNameValidity(false);
        return;
      }

      if (this.entity === "programUrl") {
        const badUrls = [
          "setup",
          "walkertracker",
          "walkz",
          "support",
          "thrives"
        ];
        if (badUrls.includes(value)) {
          this.message = `This program URL is invalid`;
          this.setAndEmitNameValidity(false);
          return;
        }
      }

      this.loading = true;
      this.$emit("loading", true);
      const response = await this.checkUniqueness({
        query,
        entity: this.entity,
        programId: this.programId
      });

      if (!this.canceled) {
        this.setAndEmitNameValidity(response.success);
        if (response.success) {
          this.message = response.data[0];
        } else {
          if (response.error) {
            this.message =
              response.error.message ||
              response.error.fields[this.entity] ||
              response.error.fields[Object.keys(response.error.fields)[0]]; // show another field related to 'this.entity', i.e. teamNameUrl
          } else {
            this.message = response;
          }
        }
      }

      this.loading = false;
      this.$emit("loading", false);
    },

    isOriginalValue(value) {
      return this.originalValue === value;
    },

    setAndEmitNameValidity(value) {
      this.isValidName = value;

      /**
       * @type {boolean}
       */
      this.$emit("validName", value);
    },

    updateName() {
      if (this.isValidName) {
        this.editName = false;
        this.message = "";
      } else {
        this.setAndEmitNameValidity(false);
      }
    }
  }
};
</script>
